import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import {
    DefaultButton,
    PrimaryButton,
    Panel,
    PanelType,
    Separator,
    Stack,
    IconButton,
    TextField,
    MessageBar,
    MessageBarType
} from '@fluentui/react';

import { Property, PropertyAttributeTypeEnum } from '../../../../../api-client';
import { RootState } from '../../../../../redux/application/redux-store';
import { DataEnchanter as DE } from '../../../../../utils/data-enchanters';
import { TRANSLATION_KEYS } from './localization/translation-keys';

import { FormProvider, IAITextField, IAIDropdown } from '../../../../../common/components/IAIForm';
import IAICheckboxField from '../../../../../common/components/IAIForm/IAICheckBox/iai-checkbox';

type FormValuesProps = Property & {
    optionVertical?: { value: string; }[]
};

type IFormDrawer = {
    customAttribute?: Property;
    disabled?: boolean;
    drawerVisible?: boolean;
    onDrawerConfirm: (dataItem: Property) => void;
    onDrawerDismiss: VoidFunction;
};

const FormDrawer: React.FunctionComponent<IFormDrawer> = ({
    customAttribute,
    disabled,
    drawerVisible,
    onDrawerConfirm,
    onDrawerDismiss
}) => {
    const {
        t: translate
    } = useTranslation();

    //#region Form-Configuration

    const formDataSchema = Yup.object().shape({
        label: Yup.string().required(translate(TRANSLATION_KEYS.AttributeErrorMessage)),
        attributeType: Yup.string().required(translate(TRANSLATION_KEYS.AttributeTypeErrorMessage)),
        areaId: Yup.string(),
        departmentId: Yup.string().when('areaId', {
            is: (areaId: any) => !!areaId,
            then: Yup.string().required(translate(TRANSLATION_KEYS.TopicErrorMessage))
        }),
        optionVertical: Yup.array().when('attributeType', {
            is: (attributeType: any) => attributeType === PropertyAttributeTypeEnum.Combo,
            then: Yup.array().min(1, translate(TRANSLATION_KEYS.OptionErrorMessage))
        }),
        defaultRender: Yup.bool(),
    });

    const defaultValues = useMemo(
        () => ({
            label: customAttribute?.label || '',
            attributeType: customAttribute?.attributeType || PropertyAttributeTypeEnum.Combo,
            areaId: customAttribute?.areaId || undefined,
            departmentId: customAttribute?.departmentId || undefined,
            optionVertical: !!customAttribute?.options?.length
                ? _.map(customAttribute?.options, x => ({ value: x }))
                : [],
            defaultRender: customAttribute?.defaultRender || false,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [customAttribute]
    );

    const methods = useForm<FormValuesProps>({ resolver: yupResolver(formDataSchema), defaultValues });

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors, isSubmitting }
    } = methods;

    const {
        fields,
        append,
        remove,
        replace
    } = useFieldArray({
        control,
        name: 'optionVertical'
    });

    useEffect(() => {
        reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customAttribute]);

    const formValues = watch();

    //#endregion

    //#region Area/Department Management

    const iaiSystem = useSelector((state: RootState) => state.system);

    const areaOptions = useMemo(
        () => _.map(iaiSystem.areas, entry => ({ key: entry.id!, text: entry.displayName! })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [iaiSystem.areas]
    );

    const topicOptions = useMemo(
        () => {
            const selectedArea = _.find(iaiSystem.areas, x => x.id === formValues.areaId);

            return !!selectedArea
                ? _.map(selectedArea.departments, entry => ({ key: entry.id!, text: entry.displayName! }))
                : [];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formValues.areaId]
    );

    //#endregion

    const [newOption, setNewOption] = React.useState<string | undefined>('');

    const onAttributeChange = React.useCallback(
        (newValue) => {
            setNewOption(newValue);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const onOptionAppend = () => {
        if (!!newOption) {
            append({ value: newOption });
            setNewOption('');
        }
    };

    useEffect(() => {
        if (formValues.attributeType != PropertyAttributeTypeEnum.Combo)
            replace([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.attributeType]);

    const _handleDismiss = () => {
        reset(defaultValues);

        onDrawerDismiss();
    }

    const _handleSubmit = (values: FormValuesProps) => {
        onDrawerConfirm({
            id: customAttribute?.id,
            label: values.label,
            attributeType: values.attributeType,
            options: !!values.optionVertical?.length
                ? _.map(values.optionVertical, x => x.value)
                : [],
            areaId: values.areaId,
            departmentId: values.departmentId,
            createdAt: !!customAttribute?.createdAt
                ? moment(customAttribute.createdAt)
                    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                    .toISOString()
                : undefined,
            defaultRender: values.defaultRender,
        });

        reset(defaultValues);
    }

    const disableCondition = disabled || isSubmitting;

    return (
        <FormProvider methods={methods}>
            <Panel
                type={PanelType.medium}
                headerText={translate(TRANSLATION_KEYS.Heading)}
                isOpen={drawerVisible}
                isBlocking={true}
                isFooterAtBottom={true}
                onDismiss={_handleDismiss}
                onRenderFooterContent={() => (
                    <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }}>
                        <DefaultButton
                            disabled={isSubmitting}
                            iconProps={{ iconName: 'Cancel' }}
                            styles={{ root: { marginRight: 10 } }}
                            onClick={_handleDismiss}
                        >
                            {translate(TRANSLATION_KEYS.CancelButtonLabel)}
                        </DefaultButton>
                        <PrimaryButton
                            type="submit"
                            disabled={disableCondition}
                            iconProps={{ iconName: 'Save' }}
                            onClick={handleSubmit(_handleSubmit)}
                        >
                            {translate(TRANSLATION_KEYS.SaveButtonText)}
                        </PrimaryButton>
                    </Stack>
                )}
            >
                <Separator />

                <Stack tokens={{ childrenGap: 10 }}>
                    <IAITextField
                        name="label"
                        label={translate(TRANSLATION_KEYS.AttributeLabel)}
                        placeholder={translate(TRANSLATION_KEYS.AttributePlaceholder)}
                        required
                        disabled={disableCondition}
                    />

                    <IAIDropdown
                        name="attributeType"
                        label={translate(TRANSLATION_KEYS.AttributeTypeLabel)}
                        placeholder={translate(TRANSLATION_KEYS.AttributeTypePlaceholder)}
                        required
                        disabled={disableCondition}
                        options={[
                            { key: PropertyAttributeTypeEnum.Combo, text: translate(DE.textByAttributeTypeEnum(PropertyAttributeTypeEnum.Combo)) },
                            { key: PropertyAttributeTypeEnum.Date, text: translate(DE.textByAttributeTypeEnum(PropertyAttributeTypeEnum.Date)) },
                            { key: PropertyAttributeTypeEnum.Input, text: translate(DE.textByAttributeTypeEnum(PropertyAttributeTypeEnum.Input)) }
                        ]}
                    />

                    <IAIDropdown
                        name="areaId"
                        label={translate(TRANSLATION_KEYS.AreaLabel)}
                        placeholder={translate(TRANSLATION_KEYS.AreaPlaceholder)}
                        disabled={disableCondition || !areaOptions.length}
                        options={areaOptions}
                        onChange={(_, option) => {
                            if (option) {
                                setValue('areaId', option.key as string);
                                setValue('departmentId', '');
                            }
                        }}
                    />

                    <IAIDropdown
                        name="departmentId"
                        label={translate(TRANSLATION_KEYS.TopicLabel)}
                        placeholder={translate(TRANSLATION_KEYS.TopicPlaceholder)}
                        disabled={disableCondition || !topicOptions.length}
                        options={topicOptions}
                    />

                    {formValues.attributeType === PropertyAttributeTypeEnum.Combo && (
                        <Stack
                            horizontal
                            horizontalAlign="space-between"
                            verticalAlign="start"
                            tokens={{ childrenGap: 15 }}
                        >
                            <Stack grow>
                                <TextField
                                    label={translate(TRANSLATION_KEYS.OptionLabel)}
                                    placeholder={translate(TRANSLATION_KEYS.OptionPlaceholder)}
                                    required
                                    disabled={disableCondition}
                                    value={newOption}
                                    onChange={(_, value) => { onAttributeChange(value); }}
                                />
                            </Stack>

                            {!disableCondition && (
                                <IconButton
                                    iconProps={{ iconName: 'Add' }}
                                    disabled={disableCondition || !newOption}
                                    styles={{ root: { marginTop: 32 } }}
                                    onClick={onOptionAppend}
                                />
                            )}
                        </Stack>
                    )}
                    {!!errors.optionVertical?.message && !disableCondition && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {errors.optionVertical.message}
                        </MessageBar>
                    )}
                    {formValues.attributeType === PropertyAttributeTypeEnum.Combo && _.map(fields, (dataItem, dataIndex) => (
                        <Stack
                            key={dataItem.id}
                            horizontal
                            horizontalAlign="space-between"
                            verticalAlign="start"
                            tokens={{ childrenGap: 15 }}
                        >
                            <Stack grow>
                                <IAITextField
                                    name={`optionVertical[${dataIndex}].value`}
                                    label={translate(TRANSLATION_KEYS.OptionLabel)}
                                    placeholder={translate(TRANSLATION_KEYS.OptionPlaceholder)}
                                    description={translate(TRANSLATION_KEYS.OptionDescription)}
                                    required
                                    disabled={disableCondition}
                                />
                            </Stack>

                            {!disableCondition && (
                                <IconButton
                                    iconProps={{ iconName: 'Blocked2' }}
                                    disabled={disableCondition}
                                    styles={{ root: { marginTop: 32 } }}
                                    onClick={() => { remove(dataIndex); }}
                                />
                            )}
                        </Stack>
                    ))}

                    <IAICheckboxField
                        name="defaultRender"
                        label={translate(TRANSLATION_KEYS.RenderAttribute)}
                    />
                </Stack>
            </Panel>
        </FormProvider>
    );
}

export default FormDrawer;